import React, { useState, useEffect, useLayoutEffect, useReducer } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { Helmet } from 'react-helmet'
import DOMPurify from 'dompurify'
import { BrPageContext, BrComponent, BrPage } from '@bloomreach/react-sdk'
import { ProductLanding } from './ProductLanding.jsx'
import OnRouteChange from '../utility/hooks/routing.js'
import Footer from '../components/footer/Footer.jsx'
import { getBaseURL } from '../../global/environments/index.js'
import SelectorLayout from '../components/selector/SelectorLayout.jsx'
import { isLocal } from '../../global/environments/index.js'
import { getPathAndEndpoint } from '../utility'
import { stripQueryParams } from '../utility/links/links-no-dependencies.js'
import { encodeForUrlComponent } from '../../global/formatters/string-formatter.js'

import {
  Banner,
  BlogArticle,
  BlogSimpleArticleList,
  Divider,
  ArticleList,
  ArticleDetail,
  Alert,
  CampaignHeader,
  Navigation,
  Search,
  Form,
  Social,
  Metamenu,
  Metadata,
  ContactUsForm,
  ResourceArticle,
  FundCards,
  FundLiterature,
  CartOrder,
  ProductListing,
  OneTrust,
  ProductDetail,
  BlogArticleList,
  Disclosures,
  BlogAuthor,
  BlogCta,
  Subsection,
  RiaLiterature,
  FormsAndApplications,
  AdvisorLogin,
  AdvisorEmailChange,
  AdvisorRegister,
  AdvisorForgotPassword,
  MyProfile,
  ProductHeader,
  Accordion,
  FeatureFund,
  SimpleEntryComponent,
  Statistics,
  Tabs,
  TextBlock,
  Wholesaler,
  WholesalerContactForm,
  HeroBanner,
  Cards,
  Headline,
  ContactForm,
  Selector,
  DisclaimerModal,
  TimerBanner,
  Disclosure,
  Carousel,
  SplitBox,
  ClipCards,
  MutualFundFinder,
} from '../components'

import {
  getDebugConfig,
  getLocalPath,
  CHANNEL_ROUTES,
  loadChannelAliases,
  areAliasesLoaded,
  LayoutContext,
  NavigationContext,
  reduceNavigationItems,
  GlobalProvider,
} from '../utility'

import { wrapMappingWithSpaComponentWrapper } from '../utility/hoc'

// Kick off the CMS aliases fetch before React boots so it is ready by first render.
loadChannelAliases()

import { fetchData } from '../data'

const customAxios = axios.create()

customAxios.interceptors.response.use(
  (response) => {
    // for cti-insitutional blog - check if document for requested blog exists
    // if document does not exist, redirect to /insights/latest-insights
    const siteTerms = ['latest-insights', 'institutional']
    const isInstitutionalBlog = siteTerms.every((term) =>
      response.data.links.self.href.includes(term)
    )
    const url = stripQueryParams(response.data.links.site.href)
    const documentExists = Object.values(response.data.page).some(
      (item) =>
        item.type === 'document' &&
        stripQueryParams(item.links?.site?.href) === url
    )

    if (isInstitutionalBlog && !documentExists) {
      globalThis.location.replace(`${getLocalPath()}/insights/latest-insights`)
      return Promise.reject(new Error('document not found'))
    }

    return response
  },
  (error) => {
    if (!error.response) return Promise.reject(error)
    return error.response
  }
)

const getBreConfig = () => {
  const _debugConfig = getDebugConfig()

  customAxios.defaults.withCredentials = true

  const _configuration = {
    path: _debugConfig.PATH,
    endpoint: _debugConfig.ENDPOINT,
    origin: _debugConfig.ORIGIN,
    debug: _debugConfig.DEBUG,
    authorizationHeader: 'HST-Authorization',
    httpClient: customAxios,
  }

  return _configuration
}

export default function Route() {
  const location = useLocation()
  const [aliasesReady, setAliasesReady] = useState(areAliasesLoaded())
  const _mapping = {
    Banner2: Banner,
    BlogSimpleArticleList,
    BlogArticleList,
    BlogArticle,
    Divider,
    Article2: ArticleDetail,
    ArticleList2: ArticleList,
    Alert2: Alert,
    Search2: Search,
    Form2: Form,
    Social2: Social,
    Metamenu2: Metamenu,
    Metadata: Metadata,
    TextBlock,
    Wholesaler,
    ContactUsForm,
    WholesalerContactForm,
    CampaignHeader,
    ResourceArticle,
    FundCards,
    FundLiterature,
    CartOrder,
    Tabs,
    ProductListing,
    OneTrust,
    ProductDetail,
    Disclosures,
    BlogAuthors: BlogAuthor,
    Subsection2: Subsection,
    RiaLiterature,
    FormsAndApplications,
    AdvisorLogin,
    AdvisorEmailChange,
    AdvisorRegister,
    AdvisorForgotPassword,
    MyProfile,
    BlogCta,
    ProductHeader,
    FeatureFund,
    Accordion2: Accordion,
    SimpleEntryComponent,
    Statistics,
    HeroBanner,
    Headline,
    Cards,
    ContactForm,
    Selector,
    DisclaimerModal,
    TimerBanner,
    Disclosure,
    CTIDisclosure: Disclosure,
    Carousel,
    SplitBox,
    SplitPanel: SplitBox,
    ClipCards,
    MutualFundFinder,
    IsZone: () => <></>,
  }

  const _wrappedMapping = wrapMappingWithSpaComponentWrapper(_mapping)

  const [navigationItems, setNavigationItems] = useReducer(
    reduceNavigationItems,
    []
  )
  const [isMenuVisible] = useState(false)

  const sanitize = DOMPurify.sanitize

  useEffect(() => {
    // Validate ?id against HAL API
    const searchParams = new URLSearchParams(globalThis.location.search)
    const id = searchParams.get('id')
    if (!id) return

    fetchData(
      `${getLocalPath()}/halapi/documents/${encodeForUrlComponent(id)}`
    ).then((data) => {
      if (!data?._meta?.path) {
        // Use canonical instead of internal path - handles /advisor
        const { path } = getPathAndEndpoint()
        const channel = CHANNEL_ROUTES.find((c) => c.path === path)
        const publicPath = channel ? (channel.canonicalPath ?? path) : path
        globalThis.location.replace(`${publicPath}/pagenotfound`)
      }
    })
  }, [])

  useEffect(() => {
    // Once CMS aliases are loaded, trigger a re-render so getBreConfig() uses the
    // full channel map. No-op if aliases were already loaded before first render.
    loadChannelAliases().then(() => setAliasesReady(true))
  }, [])

  useEffect(() => {
    setNavigationItems({ type: 'clear' })
  }, [location.pathname])

  useEffect(() => {
    // runs after page load, checks for elements that need analytics tags added
    const addAnalyticsTags = async () => {
      const analyticsClass = 'ckeditor-content-selector-analytics'
      const contentToTag = document.getElementsByClassName(analyticsClass)

      if (contentToTag?.length > 0) {
        const itemsUrl = `${getLocalPath()}/halapi/documents?_scope=/content/documents/administration/cti-common/&_expr=jcr:contains(@resourcebundle:id,%20%27ckeditor.analytics.tags%27)`
        const data = await fetchData(itemsUrl)
        const tagValues = data?._embedded?.documents?.[0]?.bundles?.default

        contentToTag.forEach((item) => {
          const innerChild = getInnermostChild(item)
          const tagKeys = Object.keys(tagValues)
          const tagValue =
            tagValues[tagKeys[tagKeys.indexOf(innerChild.innerText)]]

          if (tagValue) {
            innerChild.setAttribute('data-analytics', tagValue)
          } else {
            item.classList.remove(analyticsClass)
          }
        })
      }

      function getInnermostChild(element) {
        const children = element.children

        if (children.length > 0) {
          for (const child of children) {
            const innerChild = getInnermostChild(child)

            if (innerChild) {
              return innerChild
            }
          }
        } else {
          return element
        }
      }
    }

    window.addEventListener('load', addAnalyticsTags)

    return () => {
      window.removeEventListener('load', addAnalyticsTags)
    }
  }, [])

  useLayoutEffect(() => {
    // Fix scroll issue e.g. clicking Terms and Conditions from Home page puts you in middle of page when it should be at the top
    if (location?.state?.doScroll) {
      setTimeout(() => {
        window.scroll({
          top: 0,
          left: 0,
        })
      }, 200)
    }
  }, [location.pathname])

  const getCanonicalSite = () => {
    const { path } = getPathAndEndpoint()
    const channel = CHANNEL_ROUTES.find((c) => c.path === path)
    // null means use path as-is; '' means canonical is at root; otherwise use the override
    if (!channel) return path
    return channel.canonicalPath ?? path
  }

  const getCanonicalBase = () => {
    return isLocal()
      ? `${getBaseURL()}`
      : `${getBaseURL()}${getCanonicalSite()}`
  }

  // All hooks declared above — safe to return early now.
  if (!aliasesReady) return null

  const _config = getBreConfig()

  return (
    <>
      <OnRouteChange location={location} />
      <BrPage configuration={_config} mapping={_wrappedMapping} error={null}>
        {!isMenuVisible && (
          <main className="u-posRelative">
            <BrPageContext.Consumer>
              {(page1) => {
                const pageTitle =
                  page1.getTitle() || page1.getDocument()?.model?.data?.title
                return (
                  <GlobalProvider>
                    <Helmet>
                      {pageTitle && (
                        <title>
                          {`${pageTitle} | Columbia Threadneedle Investments US`}
                        </title>
                      )}
                      <link
                        rel="canonical"
                        href={sanitize(
                          `${getCanonicalBase()}${page1.getUrl()}`
                        )}
                      />
                    </Helmet>
                    {Object.keys(page1.model.page).find(
                      (entry) => page1.model.page[entry].name === 'tabOne'
                    ) ? (
                      // PRODUCT PAGES
                      <ProductLanding page={page1} />
                    ) : Object.keys(page1.model.page).find(
                        (entry) => page1.model.page[entry].name === 'right'
                      ) ? (
                      // DOUBLE COLUMN
                      <div>
                        <BrComponent path="above" />
                        <div className="u-flex">
                          <div
                            className={page1.isPreview() ? 'u-flexGrow2' : ''}
                          >
                            <BrComponent path="main" />
                          </div>
                          <div
                            className={`u-marginLeft5gu ${
                              page1.isPreview() ? 'u-flexGrow1' : ''
                            }`}
                          >
                            <LayoutContext.Provider
                              value={{
                                container: 'right',
                              }}
                            >
                              <BrComponent path="right" />
                            </LayoutContext.Provider>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // SINGLE COLUMN
                      <>
                        <div className="u-flex u-flexCol">
                          <NavigationContext.Provider
                            value={{
                              navigationItems: navigationItems,
                              setNavigationItems: setNavigationItems,
                            }}
                          >
                            <Navigation />
                            <BrComponent path="main">
                              {Object.values(page1.model.page).some((entry) =>
                                entry?.name?.includes('container-selector-spa')
                              ) ? (
                                <SelectorLayout />
                              ) : (
                                <div
                                  className={`u-flexAuto ${page1.isPreview() ? 'u-sizeFull' : ''}`}
                                >
                                  <BrComponent />
                                </div>
                              )}
                            </BrComponent>
                          </NavigationContext.Provider>
                        </div>
                        <div id="is-fund-detail-content-zone-6"></div>
                      </>
                    )}
                  </GlobalProvider>
                )
              }}
            </BrPageContext.Consumer>
            <BrComponent path="footer">
              <Footer />
            </BrComponent>
          </main>
        )}
      </BrPage>
    </>
  )
}
